<!-- A floating Composer expands upward on the map, including while a Workspace
     is docked right. Windowed/maximized Workspaces use the same surface in their
     right gutter. Workspace layout never expands or collapses it; switching
     conversations never remounts a page. -->
<div class="command-bar">
<div class="composer-card" [class.has-transcript]="convo.panelOpen && hasPanelContent"
     [style.height.px]="!workspaceAssistantGutter && convo.panelOpen && hasPanelContent ? panelHeight : null">
  <button type="button" class="conversation-header br-interactive"
          *ngIf="hasPanelContent"
          (click)="toggleConversation($event)"
          (pointerdown)="startResize($event)" (pointermove)="onResize($event)"
          (pointerup)="endResize()" (pointercancel)="endResize()"
          [attr.aria-expanded]="convo.panelOpen"
          [attr.aria-label]="convo.panelOpen ? 'Collapse conversation' : 'Expand conversation'">
    <span class="cc-topbar" *ngIf="convo.panelOpen && !workspaceAssistantGutter" aria-hidden="true" title="Drag to resize">
      <span class="cc-grip"></span>
    </span>
    <span class="tool-btn conversation-toggle" aria-hidden="true">
      <br-icon>{{ convo.panelOpen ? 'expand_more' : 'expand_less' }}</br-icon>
    </span>
  </button>
  <div class="conversation-overlay" *ngIf="convo.panelOpen && hasPanelContent">
  <div class="cc-messages" #transcript (scroll)="onTranscriptScroll()" [class.panel-hidden]="panelOpen" [attr.aria-hidden]="panelOpen" [attr.inert]="panelOpen ? '' : null">
    <div class="cc-message-list" #transcriptContent>
    <button type="button" class="br-btn br-btn--sm" *ngIf="convo.activeConversation.beforeSequence"
            [disabled]="convo.loadingMessages" (click)="convo.loadOlderMessages()">Load earlier messages</button>
    <div *ngFor="let m of convo.messages" class="msg" [attr.data-message-key]="transcriptMessageKey(m)"
         [class.msg-user]="m.role === 'user'" [class.msg-agent]="m.role === 'agent'">
      <span class="msg-avatar" role="img" [attr.aria-label]="m.role === 'user' ? 'You' : (m.senderName || 'BRee')">
        <img *ngIf="m.role === 'agent' && (!m.senderName || m.senderName.toLowerCase() === 'bree'); else messageAvatarIcon"
             src="assets/agents/bree/avatar/avatar-32.png"
             srcset="assets/agents/bree/avatar/avatar-32.png 1x, assets/agents/bree/avatar/avatar-64.png 2x"
             width="32" height="32" alt="" aria-hidden="true">
        <ng-template #messageAvatarIcon>
          <br-icon aria-hidden="true">{{ m.role === 'user' ? 'person' : 'smart_toy' }}</br-icon>
        </ng-template>
      </span>
      <div class="msg-content">
        <div class="msg-sender" *ngIf="m.role === 'agent'">{{ m.senderName || 'BRee' }}</div>
        <div class="br-card msg-bubble">
      <div class="msg-steps" *ngIf="m.steps?.length">
        <div class="step" *ngFor="let s of m.steps">
          <br-icon class="step-tick">check</br-icon>
          <span>{{ s }}</span>
        </div>
        <div class="step step-live" *ngIf="m.working">
          <span class="dot"></span><span class="dot"></span><span class="dot"></span>
        </div>
      </div>
      <ng-container *ngIf="m.text">
        <app-chat-markdown *ngIf="m.role === 'agent'; else plainMessage" [text]="m.text"></app-chat-markdown>
        <ng-template #plainMessage><div class="msg-text">{{ m.text }}</div></ng-template>
      </ng-container>
      <div class="step" *ngIf="m.status === 'interrupted'">Reply interrupted.</div>
      <div class="step" *ngIf="m.role === 'user' && m.status === 'failed'">Not sent.</div>
      <div class="br-flex br-items-center br-flex-wrap br-gap-2 br-mt-2" *ngIf="m.error">
        <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
                (click)="openServices()">{{ m.problem?.availableActions?.includes('sign_in') ? 'Sign in' : 'Open AI settings' }}</button>
        <span class="br-small br-text-muted" *ngIf="m.problem?.diagnosticId || m.diagnosticId">
          Diagnostic ID: <code>{{ m.problem?.diagnosticId || m.diagnosticId }}</code>
        </span>
      </div>
      <button type="button"
              class="br-btn br-btn--outline-primary br-btn--sm msg-map-locate"
              *ngIf="m.mapLocate && !m.working && !m.error"
              [attr.title]="m.mapLocate.label"
              (click)="locateOnMap(m.mapLocate)">
        <br-icon>place</br-icon>
        Locate on map
      </button>
      <ng-container *ngIf="!m.working && m.overviewAnalyses as reports">
        <button type="button"
                class="br-btn br-btn--outline-primary br-btn--sm msg-report-link"
                *ngFor="let report of reports; let i = index"
                (click)="openOverviewAnalysis(report)">
          <br-icon>description</br-icon>
          Open report{{ reports.length > 1 ? ' ' + (i + 1) : '' }}
        </button>
      </ng-container>
      <div class="msg-table-view" *ngIf="m.leadTableView as held">
        <div class="msg-table-view-title">
          <br-icon>filter_alt</br-icon>
          Proposed Leads view
        </div>
        <ul class="msg-table-view-rules" *ngIf="held.action.rules.length; else noRules">
          <li *ngFor="let rule of held.action.rules">{{ leadRuleSummary(rule) }}</li>
        </ul>
        <ng-template #noRules>
          <div class="msg-table-view-empty">Clear AI-managed filters</div>
        </ng-template>
        <div class="msg-table-view-sort" *ngIf="held.action.listSort as sort">
          List sort: {{ leadSortSummary(sort) }}
        </div>
        <div class="msg-table-view-note" *ngIf="held.action.listSort">
          List sort applies only in Leads List; Board column sorts stay unchanged.
        </div>

        <div class="msg-table-view-actions" *ngIf="!m.working && !m.error">
          <button type="button"
                  class="br-btn br-btn--primary br-btn--sm"
                  *ngIf="held.status === 'proposed'"
                  [disabled]="!convo.canApplyLeadTableView(m)"
                  (click)="convo.applyLeadTableView(m)">
            Apply view
          </button>
          <span class="msg-table-view-status" *ngIf="held.status === 'applied'">
            <br-icon>check_circle</br-icon> AI view applied
          </span>
          <button type="button"
                  class="br-btn br-btn--outline-secondary br-btn--sm"
                  *ngIf="held.status === 'applied'"
                  [disabled]="!convo.canRestoreLeadTableView(m)"
                  (click)="convo.restoreLeadTableView(m)">
            Restore previous view
          </button>
          <span class="msg-table-view-status" *ngIf="held.status === 'restored'">
            <br-icon>restore</br-icon> Previous view restored
          </span>
        </div>
        <div class="msg-table-view-feedback" *ngIf="held.feedback">{{ held.feedback }}</div>
        <div class="msg-table-view-feedback"
             *ngIf="held.status === 'proposed' && !convo.canApplyLeadTableView(m)">
          Open Leads List or Board to apply this view.
        </div>
        <div class="msg-table-view-feedback"
             *ngIf="held.status === 'applied' && !convo.canRestoreLeadTableView(m)">
          Restore is available only while the applying Leads page remains open.
        </div>
      </div>
      <div class="msg-table-view msg-email-draft" *ngIf="m.leadEmailDraft as held">
        <div class="msg-table-view-title">
          <br-icon>mail_outline</br-icon>
          Proposed email draft
        </div>
        <div class="msg-email-draft-subject">{{ held.action.subject }}</div>
        <div class="msg-email-draft-body">{{ held.action.body }}</div>
        <div class="msg-table-view-note">
          Apply only fills the open lead's local composer. Nothing is sent or recorded yet.
        </div>
        <div class="msg-table-view-actions" *ngIf="!m.working && !m.error">
          <button type="button"
                  class="br-btn br-btn--primary br-btn--sm"
                  *ngIf="held.status === 'proposed'"
                  [disabled]="!convo.canApplyLeadEmailDraft(m)"
                  (click)="convo.applyLeadEmailDraft(m)">
            Apply draft
          </button>
          <span class="msg-table-view-status" *ngIf="held.status === 'applied'">
            <br-icon>check_circle</br-icon> Draft applied locally
          </span>
        </div>
        <div class="msg-table-view-feedback" *ngIf="held.feedback">{{ held.feedback }}</div>
        <div class="msg-table-view-feedback"
             *ngIf="held.status === 'proposed' && !convo.canApplyLeadEmailDraft(m)">
          Open that lead to apply this email draft.
        </div>
      </div>
      <div class="msg-typing" *ngIf="m.working && !m.text && !m.steps?.length">
        <span class="dot"></span><span class="dot"></span><span class="dot"></span>
      </div>
        </div>
      </div>
    </div>
  </div>
  </div>
  <button type="button" class="br-btn br-icon-btn cc-scroll-bottom"
          *ngIf="showScrollToBottom && !panelOpen"
          aria-label="Scroll to bottom" title="Scroll to bottom" (click)="scrollToBottom()">
    <br-icon aria-hidden="true">arrow_downward</br-icon>
  </button>
  <app-conversation-tasks *ngIf="tasksOpen" [tasks]="tasks" [preview]="true"
                          (returnToConversation)="showConversation()" (taskAction)="previewTaskAction($event)" (taskViewed)="markTaskRead($event)">
    <ng-template let-task><app-place-enrichment-task [task]="task"></app-place-enrichment-task></ng-template>
  </app-conversation-tasks>
  <app-conversation-agents *ngIf="agentsOpen" [agents]="agents" [settings]="convo.sessions"
    [models]="agentModels" [defaultModelLabel]="modelLabel" [defaultEffortLabel]="effortLabel || 'Default'"
    (settingsChanged)="convo.updateAgentSettings($event)" (mention)="insertMention($event)" (returnToConversation)="showConversation()"></app-conversation-agents>
</div>

  <app-selection-summary *ngIf="selections.length" [selections]="selections"
                         (featureSelected)="openWorkspace($event)"
                         (clearAll)="selectionContext.clearAll()"></app-selection-summary>

  <!-- Composer: multi-line input on top, controls on their own bottom row —
       map tools live here (like the icon buttons in the Codex/Claude composer). -->
  <div class="composer">
    <textarea class="composer-input" #ci rows="1" [(ngModel)]="userInput"
              (blur)="onBlur(ci)"
              (input)="autogrow(ci); updateMentions(ci)" (click)="hasPanelContent && convo.open(); updateMentions(ci)"
              (keydown)="onComposerKeydown($event, ci)" (keyup)="onComposerKeyup($event, ci)"
              [attr.aria-controls]="mentionOptions.length ? 'composer-mentions' : null"
              [attr.aria-activedescendant]="mentionOptions.length ? 'composer-mention-' + mentionIndex : null"
              aria-autocomplete="list"
              [disabled]="!!convo.activeConversation.clearing" aria-label="Message" placeholder="What would you like to get done?"></textarea>

    <div class="mention-menu" *ngIf="mentionOptions.length" id="composer-mentions" role="listbox" aria-label="Mention an agent">
      <div *ngFor="let agent of mentionOptions; let i = index" class="br-interactive mention-option"
           role="option" [id]="'composer-mention-' + i" [attr.aria-selected]="i === mentionIndex"
           [class.br-interactive--selected]="i === mentionIndex"
           (mousedown)="$event.preventDefault()" (click)="insertMention(agent)">
        <strong>{{ agent.name }}</strong><span>{{ agent.description }}</span>
      </div>
    </div>
    <p class="br-small br-text-muted" role="alert" *ngIf="convo.voice.error">{{ convo.voice.error }}</p>
    <div class="composer-controls">
      <!-- Map tools remain with the floating Composer. A Workspace assistant
           gutter is purely chat, even though it is the same component. -->
      <div class="tools" *ngIf="!workspaceAssistantGutter">
        <button type="button" class="br-interactive tool-btn" composerTooltip="Pin location">
          <br-icon>place</br-icon>
        </button>
        <button type="button" class="br-interactive tool-btn" (click)="refresh()" composerTooltip="Refresh">
          <br-icon>refresh</br-icon>
        </button>

        <button type="button" class="br-focus-ring tool-btn"
                [class.tool-on]="inWorkspace"
                (click)="toggleWorkspace()"
                [composerTooltip]="inWorkspace ? 'Close workspace' : 'Open workspace'"
                [attr.aria-label]="inWorkspace ? 'Close workspace' : 'Open workspace'"
                [attr.aria-pressed]="inWorkspace">
          <br-icon>topic</br-icon>
        </button>
      </div>

      <span class="cc-spacer"></span>

      <button type="button" class="br-interactive provider-btn tasks-btn" (click)="toggleTasks()"
              [attr.aria-pressed]="tasksOpen" [attr.aria-label]="(tasksOpen ? 'Back to conversation' : 'Tasks') + (unreadCompletedTaskCount ? ', ' + unreadCompletedTaskCount + ' unread completed tasks' : '')">
        Tasks <span *ngIf="unreadCompletedTaskCount" class="br-badge br-badge--primary br-badge--sm tasks-unread" aria-hidden="true">{{ unreadCompletedTaskCount }}</span>
      </button>

      <button type="button" class="br-interactive provider-btn agents-btn" (click)="toggleAgents()"
              [attr.aria-pressed]="agentsOpen" aria-label="Agents">Agents</button>

      <!-- Model and effort remain separate from business agents. -->
      <div class="provider-control">
        <button type="button" class="br-interactive provider-btn model-trigger" (click)="toggleProviderMenu()"
                [attr.aria-expanded]="providerMenuOpen" [attr.aria-label]="'Choose engine or model: ' + modelLabel + (effortLabel ? ', effort: ' + effortLabel : '')">
          <span class="model-name">{{ modelLabel }}</span><span class="model-effort" *ngIf="effortLabel">{{ effortLabel }}</span>
          <br-icon class="provider-caret">{{ providerMenuOpen ? 'arrow_drop_up' : 'arrow_drop_down' }}</br-icon>
        </button>
        <div class="provider-menu" *ngIf="providerMenuOpen" (keydown.escape)="providerMenuOpen = false">
          <app-composer-model-options [models]="composerModels" [selectedId]="selectedModelId"
            [keys]="convo.chatKeys" [selectedKeyId]="convo.selectedChatKeyId" (keySelected)="pickKey($event)"
            [selectedEffort]="selectedEffort" (modelSelected)="pickModel($event)"
            (effortSelected)="selectEffort($event)"></app-composer-model-options>
        </div>
      </div>

      <button type="button" class="send-btn" *ngIf="userInput.trim()" [disabled]="convo.isWorking(convo.activeConversation) || !!convo.activeConversation.unconfirmedTurn"
              (click)="send(ci)" composerTooltip="Send" aria-label="Send message">
        <br-icon>arrow_upward</br-icon>
      </button>
      <!-- Live voice is opened and closed only from this button. -->
      <button type="button" class="send-btn" *ngIf="!userInput.trim()" (click)="toggleVoice()"
              [class.voice-on]="voiceActive" [attr.aria-pressed]="voiceActive"
              [composerTooltip]="voiceActive ? 'End Live voice' : 'Live voice'"
              [attr.aria-label]="voiceActive ? 'End Live voice with BRee' : 'Start Live voice with BRee'">
        <br-icon>graphic_eq</br-icon>
      </button>
    </div>
  </div>
</div>
</div>
